import { AvailabilitySearchQuery } from 'types';
import { ExperienceCardAction } from 'utils/searchBar';
import { LocalDate, LocalDateTime } from 'js-joda';
import { SeparatedOfferings } from 'selectors/calendarSelectors';
import { adjustDateForCutOff } from 'utils/constants';
import { consumer, safeEnumCompare } from '@tock/coretypes';
import { defaultMemoize } from 'reselect';
import {
  getTimeFormatter,
  twentyFourHourFormatter,
  twentyFourHourNakedFormatter,
} from '@tock/i18n';
import { hasPriceRanges } from 'components/business/PriceRange';
import { memoizeWithTimezoneGuard } from '@tock/shared/utils/timezone';
import { oc } from 'ts-optchain';
import AdditionalCosts from 'components/AdditionalCosts';
import _ from 'lodash';
import consumerProtos from 'utils/consumerProto';

const { Offering } = consumerProtos;

// Dates on which there are any inventory (sent from the server, includes all held dates).
export const getOpenDates = defaultMemoize((offerings: consumer.ConsumerOfferings): DateMap => {
  return _.reduce(
    offerings.openDate,
    (acc: DateMap, date) => {
      acc[date] = true;
      return acc;
    },
    {}
  );
});

export const allDates = defaultMemoize((): DateMap => {
  const allDates = [
    '2017-10-10',
    '2017-10-11',
    '2017-10-12',
    '2017-10-13',
    '2017-10-14',
    '2017-10-15',
    '2017-10-16',
    '2017-10-17',
    '2017-10-18',
    '2017-10-19',
    '2017-10-20',
  ];

  return _.reduce(
    allDates,
    (acc: DateMap, date) => {
      acc[date] = true;
      return acc;
    },
    {}
  );
});

export const allDatesInAYear = defaultMemoize((year: number): DateMap => {
  let indexDay = LocalDate.of(year, 1, 1);
  const lastDay = LocalDate.of(year + 1, 1, 1);
  const allDays: Dictionary<boolean> = {};
  while (!indexDay.equals(lastDay)) {
    allDays[indexDay.toString()] = true;
    indexDay = indexDay.plusDays(1);
  }
  return allDays;
});

export const getDateRange = defaultMemoize((offerings: consumer.ConsumerOfferings) => {
  const minDate = _.min(oc(offerings).openDate());
  const maxDate = _.max(oc(offerings).openDate());
  return {
    minDate,
    maxDate,
  };
});

export const hasNoAvailability = defaultMemoize((offerings: consumer.ConsumerOfferings) => {
  return !_.some(offerings.experience, (experience) => experience.state === 'AVAILABLE');
});

export const computeDisplayedTimes = memoizeWithTimezoneGuard(
  (
    business: consumer.BusinessConfig,
    offerings: consumer.ConsumerOfferings,
    dateString: string
  ) => {
    const times: string[] = [];
    _.forEach(business.services, (services) => {
      let cursor = adjustDateForCutOff(dateString, services.startTime as string);
      const end = adjustDateForCutOff(dateString, services.endTime as string);

      while (!cursor.isAfter(end)) {
        times.push(cursor.toString());
        cursor = cursor.plusMinutes(15);
      }
    });

    const businessTimes = _.uniq(_.sortBy(times));
    const offeredTimes = _.map(offerings.openTime, (time: string) => {
      const timeObj = adjustDateForCutOff(dateString, time);
      return timeObj.toString();
    });

    return _.map(_.sortBy(_.uniq(businessTimes.concat(offeredTimes))), (time) => {
      const value = LocalDateTime.parse(time);
      return {
        isDefault: false,
        key: twentyFourHourNakedFormatter.format(value),
        value: twentyFourHourFormatter.format(value),
        label: getTimeFormatter(business.locale).format(value),
      };
    });
  }
);

export const isOfferingReservationType = function (offering?: consumer.IOffering): boolean {
  return (
    !!offering &&
    !isOfferingEventType(offering) &&
    !isOfferingDeliveryType(offering) &&
    !isOfferingPickupType(offering) &&
    !isOfferingShippingType(offering)
  );
};

export const isOfferingEventType = function (offering?: consumer.IOffering): boolean {
  if (!offering) {
    return false;
  }
  return isEventType(offering.type || undefined);
};

export const isOfferingSeatedEventType = function (offering?: consumer.IOffering): boolean {
  if (!offering) {
    return false;
  }
  return isSeatedEventType(offering.type || undefined);
};

export const isOfferingGAEventType = function (offering?: consumer.IOffering): boolean {
  if (!offering) {
    return false;
  }
  return isGAEventType(offering.type || undefined);
};

export const isOfferingDeliveryType = function (offering?: consumer.IOffering): boolean {
  return offering?.type === 'ORDER_DELIVERY' && !offering?.reserveShippingTime;
};

export const isOfferingPickupType = function (offering?: consumer.IOffering): boolean {
  return offering?.type === 'ORDER_PICKUP';
};

export const isOnlineOrdersType = function (offering?: consumer.IOffering): boolean {
  return (
    isOfferingDeliveryType(offering) ||
    isOfferingPickupType(offering) ||
    isOfferingShippingType(offering)
  );
};

export const isOfferingShippingType = function (offering?: consumer.IOffering): boolean {
  if (offering && offering.type && offering.reserveShippingTime) {
    return offering.type === 'ORDER_DELIVERY' && offering.reserveShippingTime;
  }
  return false;
};

export const isEventType = function (type?: consumer.Offering.Type): boolean {
  if (!type) {
    return false;
  }
  return (
    safeEnumCompare(type, Offering.Type, Offering.Type.DEPRECATED_EVENT) ||
    isGAEventType(type) ||
    isSeatedEventType(type)
  );
};

export const isSeatedEventType = function (type?: consumer.Offering.Type): boolean {
  if (!type) {
    return false;
  }
  return safeEnumCompare(type, Offering.Type, Offering.Type.SEATED_EVENT);
};

export const isGAEventType = function (type?: consumer.Offering.Type): boolean {
  if (!type) {
    return false;
  }
  return safeEnumCompare(type, Offering.Type, Offering.Type.GA_EVENT);
};

export const isOfferedOnOneDateAndAtOneTime = function (offering?: consumer.IOffering): boolean {
  if (!offering) {
    return false;
  }
  return !!offering.isAtOneTime && !!offering.isOnOneDate;
};

export const getRemainingTicketsForEvent = function (
  offeringId: number,
  ticketGroups: consumer.ICalendarTicketGroup[]
) {
  let remaining = 0;
  _.forEach(ticketGroups, (ticketGroup) => {
    if (ticketGroup?.ticketTypePrice?.[0].ticketTypeId === offeringId) {
      remaining += ticketGroup?.availableTickets || 0;
    }
  });

  return remaining;
};

export const getRemainingEventTicketText = function (ticketCount: number) {
  return ticketCount > 0 && ticketCount <= 10 ? `Only ${ticketCount} remaining` : undefined;
};

export const hasMultipleOfferingTypes = (separatedOfferings: SeparatedOfferings) => {
  const numberOfOfferingTypes = (
    Object.keys(separatedOfferings) as Array<keyof typeof separatedOfferings>
  ).reduce((acc, offeringKey) => {
    if (separatedOfferings[offeringKey]) {
      const list = separatedOfferings[offeringKey];
      return list && list.length > 0 ? acc + 1 : acc;
    }
    return acc;
  }, 0);
  return numberOfOfferingTypes > 1;
};

/**
 * Warning!
 * The logic for counting items needs to match the logic for displaying the items in
 * templates EventOfferingDetailsSimple.tsx and ExperienceResultSimple.tsx
 */
export const getOfferingLineItemsCount = (offering: consumer.Offering) => {
  const { isAmexOnly, largePartyConfig, type, isOnOneDate, eventDetails } = offering;

  const isEvent = isEventType(type);

  const price = offering.price as consumer.OfferedPriceSummary;

  let count = 0;
  if (isAmexOnly) {
    count += 1;
  }

  if (isEvent) {
    if (isOnOneDate || eventDetails?.startTime || eventDetails?.eventFirstDatetimeSecs) {
      count += 1;
    }

    const hasSchedule = !_.isNil(eventDetails?.schedule);
    const hasDate = !!eventDetails?.date;

    if (hasSchedule) {
      count += 1;
    }

    if (!hasSchedule && hasDate) {
      count += 1;
    }
  }

  if (price && hasPriceRanges(price) && type !== 'RESERVATION') {
    count += 1;
  }

  if (price && AdditionalCosts.hasPriceRanges(price)) {
    count += 1;
  }

  if (largePartyConfig) {
    count += 1;
  }

  const showEventSize =
    isOfferingSeatedEventType(offering) ||
    (!isOfferingGAEventType(offering) && !isOfferedOnOneDateAndAtOneTime(offering));

  if (showEventSize) {
    count += 1;
  }

  return count;
};

export interface GetResultItmeSizeOptions {
  index: number;
  results: consumer.BusinessAvailabilitySearchResponse;
  hideExperiences: boolean;
  matchesMd: boolean;
}

/**
 * used in conjunction with react-window
 * @param {GetResultItmeSizeOptions} options - values for computing the size
 * @returns
 */
export const getResultItemSize = ({
  index,
  results,
  hideExperiences,
  matchesMd,
}: GetResultItmeSizeOptions): number => {
  const resultItem = results?.offeringAvailability
    ? results.offeringAvailability[index]
    : undefined;
  let lineItemCounts = 0;
  let highestOfferingLineItemCount = 0;
  let hasPrice = false;
  let isReservationType = false;
  for (const searchOffering of resultItem?.offering ?? []) {
    let count = getOfferingLineItemsCount(searchOffering.offering as consumer.Offering);

    // Subtract 1 to because original default size already accounts for 1 line item
    if (count > 0) {
      count -= 1;
    }

    lineItemCounts += count;
    if (count > highestOfferingLineItemCount) {
      highestOfferingLineItemCount = count;
    }
    hasPrice = (searchOffering.ticketGroup ?? []).some((group) => (group?.price ?? 0) > 0);
    if (searchOffering.offering) {
      isReservationType = isOfferingReservationType(searchOffering.offering);
    }
  }

  const offeringLength = resultItem?.offering?.length ?? 0;
  const hasMultipleOfferings = offeringLength > 1;

  /**
   * Be careful here, please check this against any styling changes made to the
   * AvailabilitySearchResultSimple component.
   *
   * FYI - note that `matchesMd` changes values about halfway through this function
   * which does alter calculations
   */
  const itemPadding = 24; // bottom padding for container of biz header and experiences
  const businessHeaderHeight = !matchesMd ? 96 : 162;
  let baseOfferingHeight = !matchesMd ? 136 : 156;
  if (hasPrice && isReservationType) {
    baseOfferingHeight += 18; // price below the time items
  }
  const baseOfferingPadding = 8;
  const lineItemHeight = !matchesMd ? 28 : 30;
  const countTextHeight = 16;

  // check if cards are horizontal at lower media queries
  let totalLineItemHeights = !matchesMd
    ? lineItemHeight * highestOfferingLineItemCount
    : lineItemHeight * lineItemCounts;
  let totalBaseOfferingHeight = !matchesMd
    ? baseOfferingHeight
    : offeringLength * baseOfferingHeight;
  let totalOfferingPadding = !matchesMd
    ? hasMultipleOfferings
      ? baseOfferingPadding
      : 0
    : hasMultipleOfferings
      ? baseOfferingPadding * (resultItem?.offering?.length ?? 0 - 1)
      : 0;

  const totalCountTextHeight = !matchesMd && hasMultipleOfferings ? countTextHeight : 0;

  // happens for initial togo page, when no date is selected
  if (hideExperiences) {
    totalBaseOfferingHeight = 0;
    totalOfferingPadding = 0;
    totalLineItemHeights = 0;
  }

  return (
    itemPadding +
    businessHeaderHeight +
    totalBaseOfferingHeight +
    totalOfferingPadding +
    totalLineItemHeights +
    totalCountTextHeight
  );
};

/**
 * Render a CTA instead of available times when appropriate,
 * e.g., if the query does not contain a party size or includes a date range
 */
export const shouldRenderCTAFromActionAndQuery = (
  experienceCardAction: ExperienceCardAction,
  query: AvailabilitySearchQuery
): boolean => {
  return !!(experienceCardAction === 'BOOK_NOW' || !query.size || query.dateEnd);
};
